/**
 * Authentication store using Zustand.
 * Manages user authentication state, login, logout, and registration.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { logError } from '@utils/logger'
import { authAPI } from '@api/auth'
import { User, LoginRequest, RegisterRequest } from '@types/index'

interface AuthState {
  // State
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  login: (credentials: LoginRequest) => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Login action
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null })
        try {
          await authAPI.login(credentials)
          
          // SECURITY: Token is now stored in HttpOnly cookie by backend
          // Do NOT store in localStorage - cookie is more secure (XSS protection)
          // Remove any old localStorage token
          localStorage.removeItem('auth_token')
          
          // Clear React Query cache to ensure fresh data for the new user
          // This prevents showing previous user's cached data
          try {
            const { queryClient } = await import('@/lib/queryClient')
            queryClient.clear() // Clear all cached queries
          } catch (error) {
            logError('Failed to clear React Query cache:', error)
          }
          
          // Get user info
          const user = await authAPI.getCurrentUser()
          
          set({
            user,
            token: null, // SECURITY: Token is in HttpOnly cookie, not stored in state
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error: any) {
          const errorMessage = error.response?.data?.detail || error.message || 'Login failed'
          // Clear any localStorage token on error
          localStorage.removeItem('auth_token')
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          })
          throw error
        }
      },

      // Register action
      register: async (data: RegisterRequest) => {
        set({ isLoading: true, error: null })
        try {
          await authAPI.register(data)
          
          // Auto-login after registration
          await get().login({
            email: data.email,
            password: data.password,
          })
        } catch (error: any) {
          // Handle validation errors (422) - detail can be an array of error objects
          let errorMessage = 'Registration failed'
          
          if (error.response?.data?.detail) {
            if (Array.isArray(error.response.data.detail)) {
              // Format validation errors into readable message
              errorMessage = error.response.data.detail
                .map((errorObj: any) => {
                  const field = errorObj.loc && errorObj.loc.length > 1 
                    ? errorObj.loc[errorObj.loc.length - 1] 
                    : 'field'
                  // Remove "Value error, " prefix from Pydantic error messages
                  let msg = errorObj.msg || ''
                  msg = msg.replace(/^Value error,\s*/i, '').trim()
                  // Capitalize first letter for better readability
                  msg = msg.charAt(0).toUpperCase() + msg.slice(1)
                  return `${field}: ${msg}`
                })
                .join(', ')
            } else if (typeof error.response.data.detail === 'string') {
              errorMessage = error.response.data.detail
            } else {
              // Fallback: convert to string if it's an object
              errorMessage = JSON.stringify(error.response.data.detail)
            }
          } else if (error.message) {
            errorMessage = error.message
          }
          
          // Ensure errorMessage is always a string
          const safeErrorMessage = typeof errorMessage === 'string' ? errorMessage : 'Registration failed'
          set({
            isLoading: false,
            error: safeErrorMessage,
          })
          throw error
        }
      },

      // Logout action
      logout: async () => {
        try {
          // Call backend logout endpoint to clear HttpOnly cookie
          await authAPI.logout()
        } catch (error) {
          // Even if logout API call fails, clear local state
          logError('Logout API call failed:', error)
        }
        
        // Clear localStorage token (if any - for backward compatibility)
        localStorage.removeItem('auth_token')
        
        // Cancel all in-flight queries and clear React Query cache
        try {
          const { queryClient } = await import('@/lib/queryClient')
          // Cancel all in-flight queries first to prevent 401 errors
          queryClient.cancelQueries()
          // Then clear the cache
          queryClient.clear()
        } catch (error) {
          logError('Failed to clear React Query cache:', error)
        }
        
        // Clear local state
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        })
      },

      // Check authentication status
      checkAuth: async () => {
        // SECURITY: No longer check localStorage - rely on HttpOnly cookie
        // Try to get user info - if cookie is valid, this will succeed
        set({ isLoading: true })
        try {
          const user = await authAPI.getCurrentUser()
          // Cookie is valid, user is authenticated
          set({
            user,
            token: null, // Token is in cookie, not stored in state
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          // Cookie is invalid or missing, user is not authenticated
          // Clear any old localStorage token
          localStorage.removeItem('auth_token')
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // SECURITY: Do NOT persist token - it's in HttpOnly cookie
        // Only persist user info and auth status
        user: state.user,
        // token: state.token, // REMOVED - token is in HttpOnly cookie, not localStorage
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

